import { config } from '@psl/functions/config';
import format from 'date-fns/format';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { configLoggerType, logger } from 'react-native-logs';
import { isIosSimulator } from '../functions/deviceInfo/isSimulator';

const defaultLogger = createLogger({});
const assignedColors: { [p: string]: string } = {};
const debuggers: { [p: string]: Debugger } = {};

const availableColors: any = {
  black: 30,
  red: 31,
  green: 32,
  yellow: 33,
  blue: 34,
  magenta: 35,
  cyan: 36,
  white: 37,
  grey: 90,
  redBright: 91,
  greenBright: 92,
  yellowBright: 93,
  blueBright: 94,
  magentaBright: 95,
  cyanBright: 96,
  whiteBright: 97,
};

const kSupportedColors = [
  'black',
  'red',
  'green',
  'yellow',
  'blue',
  'magenta',
  'cyan',
  'grey',
  'redBright',
  'greenBright',
  'yellowBright',
  'blueBright',
  'magentaBright',
  'cyanBright',
];

let nextColorIndex = 0;

const debugColor = (name: string) => {
  let color = assignedColors[name];
  if (!color) {
    color = kSupportedColors[nextColorIndex++ % kSupportedColors.length];
    assignedColors[name] = color;
  }
  return color;
};

export type Debugger = {
  (s: string, ...args: any[]): void;
  success: (s: string, ...args: any[]) => void;
  error: (s: string, ...args: any[]) => void;
  warn: (s: string, ...args: any[]) => void;
  info: (s: string, ...args: any[]) => void;
  verbose: (s: string, ...args: any[]) => void;
};

function debuggerFactory(
  enabled: boolean,
  log: (s: string, ...args: any[]) => void,
  color?: string,
): (s: string, ...args: any[]) => void {
  return (s: string, ...args: any[]) => {
    if (!enabled || (config.isProd && !__DEV__)) return;
    let index = 0;
    s = s.replace(/%[a-zA-Z%]/g, match => {
      if (match === '%%') return '%';
      let arg = args[index++];

      if (typeof arg === 'function') {
        arg = arg();
      }

      if (arg === null) {
        return 'null';
      }
      if (arg === undefined) {
        return 'undefined';
      }

      if (match === '%o') {
        return JSON.stringify(arg);
      }

      if (match === '%O') {
        return JSON.stringify(arg, null, 2);
      }

      if (match === '%d' || match === '%f') {
        return Number(arg);
      }

      return arg;
    });

    // handle remaining args
    const device =
      Platform.OS === 'web' ? '' : `\x1b[0;35m[${debugDeviceName()}]`;

    if (Platform.OS === 'web') {
      if (color && availableColors[color]) {
        s = `\x1b[0;${availableColors[color]}m${s}\x1b[0m`;
      }

      log(
        `${device}\x1b[0m \x1b[0;90m${s}\x1b[0m`,
        ...args
          .slice(index)
          .map(arg => (typeof arg === 'function' ? arg() : arg)),
      );
    } else {
      // handle remaining args
      s +=
        ' ' +
        args
          .slice(index)
          .map(arg => (typeof arg === 'function' ? arg() : arg))
          .join(' ');

      if (color && availableColors[color]) {
        s = `\x1b[0;${availableColors[color]}m${s}\x1b[0m`;
      }

      log(`${device}\x1b[0m \x1b[0;90m${s}\x1b[0m`);
    }
  };
}

export function makeDebug(
  name: string,
  enabled = false,
  verbose = false,
): Debugger {
  if (debuggers[name]) {
    return debuggers[name];
  }

  const log =
    enabled && (!config.isProd || __DEV__)
      ? createLogger({
          enabled: true,
          printLevel: false,
          dateFormat: date => format(date, 'HH:mm:ss') + ' ',
          transportOptions: {
            extensionColors: {
              [name]: debugColor(name),
            },
          },
        }).extend(name)
      : defaultLogger;

  const debug = debuggerFactory(enabled, (...args) =>
    log.debug(...args),
  ) as Debugger;
  debug.success = debuggerFactory(
    enabled,
    (...args) => log.info(...args),
    'green',
  );
  debug.info = debuggerFactory(enabled, (...args) => log.info(...args), 'cyan');
  debug.warn = debuggerFactory(
    enabled,
    (...args) => log.warn(...args),
    'yellow',
  );
  debug.error = debuggerFactory(
    enabled,
    (...args) => log.error(...args),
    'red',
  );
  debug.verbose = debuggerFactory(
    enabled && verbose,
    s => log.debug(s),
    'blue',
  );
  debuggers[name] = debug;
  return debug;
}

export function debugDeviceName() {
  return Platform.OS === 'web'
    ? 'browser'
    : ((isIosSimulator() ? Device.deviceName : Device.modelName) ??
        Device.manufacturer);
}

export function debugFormatTime(
  value: Date | number | undefined,
  suffix: 'ms' | 's' | 'none' = 's',
) {
  if (!value) return '(nil)';
  const s = format(value, 'HH:mm:ss.SSS');
  if (suffix === 'ms') return `${s} (${+new Date(value)})`;
  if (suffix === 's') return `${s} (${new Date(value).getTime() / 1000})`;
  return s;
}

function createLogger(params: configLoggerType) {
  return logger.createLogger(params);
}
